import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import axios from 'axios';
import {
  Clock,
  Edit3,
  History,
  MessageSquare,
  RefreshCw,
  Search,
  Send,
  Users,
  X
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

function formatarData(valor) {
  if (!valor) return '—';

  return new Date(valor).toLocaleString(
    'pt-BR',
    {
      dateStyle: 'short',
      timeStyle: 'short'
    }
  );
}

function formatarDinheiro(valor) {
  return Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  );
}

function formatarEtapa(etapa) {
  return String(etapa || 'NÃO INFORMADA')
    .replace(/_/g, ' ');
}

function CardResumo({
  titulo,
  valor,
  detalhe,
  icone,
  darkMode
}) {
  return (
    <div className={
      `${
        darkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      } border rounded-xl p-5 shadow-sm`
    }>
      <div className="flex items-center gap-4">
        <div className={
          `${
            darkMode
              ? 'bg-gray-700'
              : 'bg-blue-50'
          } p-3 rounded-lg text-blue-600`
        }>
          {icone}
        </div>

        <div>
          <p className={
            `${
              darkMode
                ? 'text-gray-400'
                : 'text-gray-500'
            } text-sm`
          }>
            {titulo}
          </p>

          <p className={
            `${
              darkMode
                ? 'text-white'
                : 'text-gray-900'
            } text-2xl font-bold`
          }>
            {valor}
          </p>

          {detalhe && (
            <p className={
              `${
                darkMode
                  ? 'text-gray-500'
                  : 'text-gray-400'
              } text-xs mt-1`
            }>
              {detalhe}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Remarketing() {
  const { darkMode } = useSettings();

  const [leads, setLeads] = useState([]);
  const [resumo, setResumo] = useState({
    elegiveis: 0,
    sem_tentativa: 0,
    com_tentativa: 0,
    potencial: 0
  });

  const [loading, setLoading] = useState(true);
  const [enviandoId, setEnviandoId] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] =
    useState('TODOS');

  const [leadSelecionado, setLeadSelecionado] =
    useState(null);

  const [mensagem, setMensagem] =
    useState('');

  const [historico, setHistorico] =
    useState([]);

  const [historicoLoading, setHistoricoLoading] =
    useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const resposta = await axios.get(
        `${API}/remarketing`
      );

      setLeads(resposta.data?.data || []);
      setResumo(
        resposta.data?.resumo || {
          elegiveis: 0,
          sem_tentativa: 0,
          com_tentativa: 0,
          potencial: 0
        }
      );
    } catch (error) {
      console.error(
        'Erro ao carregar Remarketing:',
        error
      );

      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const leadsFiltrados = useMemo(() => {
    const texto = busca
      .trim()
      .toLowerCase();

    return leads.filter(lead => {
      const tentativas =
        Number(lead.tentativas || 0);

      let correspondeFiltro = true;

      if (filtro === 'SEM_TENTATIVA') {
        correspondeFiltro =
          tentativas === 0;
      } else if (filtro === 'UMA_TENTATIVA') {
        correspondeFiltro =
          tentativas === 1;
      } else if (filtro === 'DUAS_TENTATIVAS') {
        correspondeFiltro =
          tentativas === 2;
      } else if (filtro === 'TRES_OU_MAIS') {
        correspondeFiltro =
          tentativas >= 3;
      }

      const correspondeBusca =
        !texto ||
        String(lead.nome || '')
          .toLowerCase()
          .includes(texto) ||
        String(lead.phone || '')
          .includes(busca.trim()) ||
        String(lead.cpf_cnpj || '')
          .includes(busca.trim()) ||
        String(lead.stage || '')
          .toLowerCase()
          .includes(texto);

      return (
        correspondeFiltro &&
        correspondeBusca
      );
    });
  }, [leads, busca, filtro]);

  const abrirLead = async lead => {
    setLeadSelecionado(lead);
    setMensagem(
      lead.mensagem_sugerida || ''
    );

    setHistoricoLoading(true);
    setHistorico([]);

    try {
      const resposta = await axios.get(
        `${API}/remarketing/${lead.id}/historico`
      );

      setHistorico(
        resposta.data?.data || []
      );
    } catch (error) {
      console.error(
        'Erro ao carregar histórico:',
        error
      );
    } finally {
      setHistoricoLoading(false);
    }
  };

  const fecharLead = () => {
    setLeadSelecionado(null);
    setMensagem('');
    setHistorico([]);
  };

  const enviarMensagem = async lead => {
    if (!mensagem.trim()) {
      window.alert(
        'Digite uma mensagem antes de enviar.'
      );
      return;
    }

    const confirmar = window.confirm(
      `Enviar esta mensagem para ${
        lead.nome || lead.phone
      }?`
    );

    if (!confirmar) return;

    setEnviandoId(lead.id);

    try {
      await axios.post(
        `${API}/remarketing/${lead.id}/enviar`,
        {
          message: mensagem.trim()
        }
      );

      window.alert(
        'Mensagem enviada para a fila do WhatsApp.'
      );

      await carregar();
      await abrirLead({
        ...lead,
        mensagem_sugerida: mensagem
      });
    } catch (error) {
      const erro =
        error.response?.data?.error ||
        error.message;

      window.alert(
        `Não foi possível enviar: ${erro}`
      );
    } finally {
      setEnviandoId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={
            `${
              darkMode
                ? 'text-white'
                : 'text-gray-900'
            } text-3xl font-bold`
          }>
            Remarketing
          </h1>

          <p className={
            darkMode
              ? 'text-gray-400'
              : 'text-gray-600'
          }>
            Recuperação manual de oportunidades
            comerciais que abandonaram o atendimento
          </p>
        </div>

        <button
          onClick={carregar}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw
            size={17}
            className={
              loading ? 'animate-spin' : ''
            }
          />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <CardResumo
          titulo="Leads elegíveis"
          valor={resumo.elegiveis || 0}
          icone={<Users size={24} />}
          darkMode={darkMode}
        />

        <CardResumo
          titulo="Nunca contatados"
          valor={resumo.sem_tentativa || 0}
          icone={<Clock size={24} />}
          darkMode={darkMode}
        />

        <CardResumo
          titulo="Já contatados"
          valor={resumo.com_tentativa || 0}
          icone={<MessageSquare size={24} />}
          darkMode={darkMode}
        />

        <CardResumo
          titulo="Potencial comercial"
          valor={formatarDinheiro(
            resumo.potencial
          )}
          detalhe="Valor estimado das oportunidades"
          icone={<RefreshCw size={24} />}
          darkMode={darkMode}
        />
      </div>

      <div className={
        `${
          darkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border rounded-xl p-4 shadow-sm`
      }>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search
              size={18}
              className={
                `absolute left-3 top-1/2 -translate-y-1/2 ${
                  darkMode
                    ? 'text-gray-500'
                    : 'text-gray-400'
                }`
              }
            />

            <input
              value={busca}
              onChange={event =>
                setBusca(event.target.value)
              }
              placeholder="Buscar nome, telefone, CPF ou etapa..."
              className={
                `w-full pl-10 pr-4 py-2.5 rounded-lg border outline-none ${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`
              }
            />
          </div>

          {[
            ['TODOS', 'Todos'],
            ['SEM_TENTATIVA', 'Sem tentativa'],
            ['UMA_TENTATIVA', '1 tentativa'],
            ['DUAS_TENTATIVAS', '2 tentativas'],
            ['TRES_OU_MAIS', '3 ou mais']
          ].map(([id, texto]) => (
            <button
              key={id}
              onClick={() => setFiltro(id)}
              className={
                `px-3 py-2 rounded-lg text-sm font-medium border ${
                  filtro === id
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : darkMode
                      ? 'bg-gray-900 border-gray-700 text-gray-300'
                      : 'bg-white border-gray-300 text-gray-700'
                }`
              }
            >
              {texto}
            </button>
          ))}
        </div>
      </div>

      <div className={
        `${
          darkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border rounded-xl shadow-sm overflow-hidden`
      }>
        {loading ? (
          <div className={
            `${
              darkMode
                ? 'text-gray-400'
                : 'text-gray-500'
            } text-center py-14`
          }>
            Carregando oportunidades...
          </div>
        ) : leadsFiltrados.length === 0 ? (
          <div className={
            `${
              darkMode
                ? 'text-gray-400'
                : 'text-gray-500'
            } text-center py-14`
          }>
            <MessageSquare
              size={34}
              className="mx-auto mb-3 opacity-50"
            />

            Nenhum lead elegível para Remarketing.

            <div className="text-xs mt-2">
              Clientes pagos, contratados ou em
              produção são excluídos automaticamente.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className={
                darkMode
                  ? 'bg-gray-700'
                  : 'bg-gray-50'
              }>
                <tr className={
                  `${
                    darkMode
                      ? 'text-gray-300'
                      : 'text-gray-500'
                  } text-left text-xs uppercase`
                }>
                  <th className="px-5 py-3">
                    Cliente
                  </th>
                  <th className="px-5 py-3">
                    Etapa
                  </th>
                  <th className="px-5 py-3">
                    Última interação
                  </th>
                  <th className="px-5 py-3">
                    Tempo parado
                  </th>
                  <th className="px-5 py-3">
                    Tentativas
                  </th>
                  <th className="px-5 py-3">
                    Último envio
                  </th>
                  <th className="px-5 py-3">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {leadsFiltrados.map(lead => (
                  <tr
                    key={lead.id}
                    className={
                      `${
                        darkMode
                          ? 'border-gray-700 hover:bg-gray-700'
                          : 'border-gray-100 hover:bg-gray-50'
                      } border-t`
                    }
                  >
                    <td className="px-5 py-4">
                      <div className={
                        `${
                          darkMode
                            ? 'text-white'
                            : 'text-gray-900'
                        } font-semibold`
                      }>
                        {lead.nome || 'Sem nome'}
                      </div>

                      <div className={
                        `${
                          darkMode
                            ? 'text-gray-400'
                            : 'text-gray-500'
                        } text-xs mt-1`
                      }>
                        {lead.phone || 'Sem telefone'}
                      </div>

                      <div className={
                        `${
                          darkMode
                            ? 'text-gray-500'
                            : 'text-gray-400'
                        } text-xs`
                      }>
                        {lead.cpf_cnpj || 'CPF não informado'}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <span className="inline-block bg-yellow-100 text-yellow-800 px-2.5 py-1 rounded-full text-xs font-semibold">
                        {formatarEtapa(lead.stage)}
                      </span>
                    </td>

                    <td className={
                      `${
                        darkMode
                          ? 'text-gray-300'
                          : 'text-gray-600'
                      } px-5 py-4 text-sm`
                    }>
                      {formatarData(
                        lead.ultima_interacao
                      )}
                    </td>

                    <td className={
                      `${
                        darkMode
                          ? 'text-gray-300'
                          : 'text-gray-600'
                      } px-5 py-4 text-sm`
                    }>
                      {lead.horas_sem_interacao || 0}
                      {' '}hora(s)
                    </td>

                    <td className={
                      `${
                        darkMode
                          ? 'text-gray-300'
                          : 'text-gray-600'
                      } px-5 py-4 text-sm`
                    }>
                      {lead.tentativas || 0}
                    </td>

                    <td className={
                      `${
                        darkMode
                          ? 'text-gray-300'
                          : 'text-gray-600'
                      } px-5 py-4 text-sm`
                    }>
                      {formatarData(
                        lead.ultimo_remarketing
                      )}
                    </td>

                    <td className="px-5 py-4">
                      <button
                        onClick={() => abrirLead(lead)}
                        className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm"
                      >
                        <Edit3 size={15} />
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {leadSelecionado && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={fecharLead}
          />

          <div className={
            `${
              darkMode
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-900'
            } relative ml-auto w-full max-w-xl h-full shadow-2xl overflow-y-auto`
          }>
            <div className={
              `${
                darkMode
                  ? 'border-gray-700'
                  : 'border-gray-200'
              } sticky top-0 bg-inherit border-b p-5 flex items-start justify-between`
            }>
              <div>
                <h2 className="text-xl font-bold">
                  {leadSelecionado.nome ||
                    'Lead sem nome'}
                </h2>

                <p className={
                  darkMode
                    ? 'text-gray-400 text-sm'
                    : 'text-gray-500 text-sm'
                }>
                  {leadSelecionado.phone}
                  {' · '}
                  {formatarEtapa(
                    leadSelecionado.stage
                  )}
                </p>
              </div>

              <button
                onClick={fecharLead}
                className={
                  darkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }
              >
                <X size={23} />
              </button>
            </div>

            <div className="p-5 space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Mensagem de Remarketing
                </label>

                <textarea
                  value={mensagem}
                  onChange={event =>
                    setMensagem(event.target.value)
                  }
                  rows={9}
                  className={
                    `w-full rounded-lg border p-3 outline-none resize-y ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`
                  }
                />

                <div className="flex justify-end mt-3">
                  <button
                    onClick={() =>
                      enviarMensagem(
                        leadSelecionado
                      )
                    }
                    disabled={
                      enviandoId ===
                      leadSelecionado.id
                    }
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Send size={17} />

                    {enviandoId ===
                    leadSelecionado.id
                      ? 'Enviando...'
                      : 'Enviar WhatsApp'}
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <History size={18} />

                  <h3 className="font-bold">
                    Histórico de envios
                  </h3>
                </div>

                {historicoLoading ? (
                  <div className={
                    darkMode
                      ? 'text-gray-400 text-sm'
                      : 'text-gray-500 text-sm'
                  }>
                    Carregando histórico...
                  </div>
                ) : historico.length === 0 ? (
                  <div className={
                    `${
                      darkMode
                        ? 'bg-gray-800 text-gray-400'
                        : 'bg-gray-50 text-gray-500'
                    } rounded-lg p-4 text-sm`
                  }>
                    Nenhuma mensagem de Remarketing
                    foi enviada para este lead.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historico.map(item => (
                      <div
                        key={item.id}
                        className={
                          `${
                            darkMode
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-gray-50 border-gray-200'
                          } border rounded-lg p-4`
                        }
                      >
                        <div className="flex justify-between gap-3">
                          <div className="font-semibold text-sm">
                            {formatarEtapa(item.stage)}
                          </div>

                          <div className={
                            darkMode
                              ? 'text-gray-400 text-xs'
                              : 'text-gray-500 text-xs'
                          }>
                            {formatarData(item.sent_at)}
                          </div>
                        </div>

                        <div className={
                          `${
                            item.delivered
                              ? 'text-green-600'
                              : 'text-yellow-600'
                          } text-xs font-semibold mt-2`
                        }>
                          {item.delivered
                            ? 'Enviado para a fila'
                            : 'Não confirmado'}
                        </div>

                        {item.metadata?.mensagem && (
                          <div className={
                            `${
                              darkMode
                                ? 'text-gray-300'
                                : 'text-gray-600'
                            } whitespace-pre-wrap text-sm mt-3`
                          }>
                            {item.metadata.mensagem}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
